<template>
  <div class="date-component">
    <div class="floating-input-container">
      <input 
        :value="displayValue"
        @input="onInputChange"
        @blur="onBlur"
        @focus="onFocus"
        class="date-input"
        :class="{ 'error': hasError, 'has-value': displayValue }"
        placeholder=" "
        maxlength="10"
        :id="fieldId"
        autocomplete="off"
        autocorrect="off"
        autocapitalize="off"
        spellcheck="false"
        ref="dateInput"
      />
      <label :for="fieldId" class="floating-label" :class="{ 'float': displayValue || isFocused }">
        {{ label }}<span v-if="required">*</span>
      </label>
      <IconProvider name="Calendar" class="calendar-icon" @click.stop="toggleDatePicker" />
    </div>
    <span v-if="hasError" class="error-message">{{ errorMessage }}</span>
    <!-- Custom Date Picker -->
    <div v-if="showDatePicker" class="custom-date-picker" @click.stop>
      <!-- Header with navigation -->
      <div class="picker-header">
        <button @click="previousPeriod" class="nav-button">&lt;</button>
        <div class="period-display" @click="toggleView">
          <span v-if="currentView === 'date'">{{ currentMonthYear }}</span>
          <span v-else-if="currentView === 'month'">{{ currentYear }}</span>
          <span v-else>{{ yearRangeDisplay }}</span>
        </div>
        <button @click="nextPeriod" class="nav-button">&gt;</button>
      </div>
      
      <!-- Date View -->
      <div v-if="currentView === 'date'" class="date-grid">
        <div class="weekdays">
          <div v-for="day in weekdays" :key="day" class="weekday">{{ day }}</div>
        </div>
        <div class="dates">
          <div 
            v-for="date in calendarDates" 
            :key="date.key"
            @click="selectDate(date)"
            class="date-cell"
            :class="{ 
              'other-month': !date.currentMonth,
              'selected': isSelectedDate(date),
              'today': isToday(date),
              'disabled': isDateDisabled(date)
            }"
          >
            {{ date.day }}
          </div>
        </div>
      </div>
      
      <!-- Month View -->
      <div v-if="currentView === 'month'" class="month-grid">
        <div 
          v-for="month in months" 
          :key="month.value"
          @click="selectMonth(month)"
          class="month-cell"
          :class="{ 'selected': month.value === selectedMonth }"
        >
          {{ month.name }}
        </div>
      </div>
      
      <!-- Year View -->
      <div v-if="currentView === 'year'" class="year-grid">
        <div 
          v-for="year in years" 
          :key="year"
          @click="selectYear(year)"
          class="year-cell"
          :class="{ 'selected': year === selectedYear }"
        >
          {{ year }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import IconProvider from './IconProvider.vue';

export default {
  name: 'DateComponent',
  components: {
    IconProvider
  },
  props: {
    value: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: 'Date'
    },
    required: {
      type: Boolean,
      default: false
    },
    minDate: {
      type: String,
      default: null
    },
    maxDate: {
      type: String,
      default: null
    },
    fieldId: {
      type: String,
      default: 'date-field'
    },
    podDateMissing: {
      type: Boolean,
      default: false
    },
    compName: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      displayValue: this.value || '',
      isFocused: false,
      hasError: false,
      errorMessage: '',
      showDatePicker: false,
      currentView: 'date', // 'date', 'month', 'year'
      selectedDate: new Date(),
      selectedMonth: new Date().getMonth(),
      selectedYear: new Date().getFullYear(),
      isSelectingFromPicker: false,
      weekdays: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
      months: [
        { name: 'Jan', value: 0 }, { name: 'Feb', value: 1 }, { name: 'Mar', value: 2 },
        { name: 'Apr', value: 3 }, { name: 'May', value: 4 }, { name: 'Jun', value: 5 },
        { name: 'Jul', value: 6 }, { name: 'Aug', value: 7 }, { name: 'Sep', value: 8 },
        { name: 'Oct', value: 9 }, { name: 'Nov', value: 10 }, { name: 'Dec', value: 11 }
      ]
    }
  },
  computed: {
    currentMonthYear() {
      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'];
      return `${monthNames[this.selectedMonth]} ${this.selectedYear}`;
    },
    currentYear() {
      return this.selectedYear.toString();
    },
    yearRangeDisplay() {
      const startYear = Math.floor(this.selectedYear / 10) * 10;
      return `${startYear} - ${startYear + 9}`;
    },
    calendarDates() {
      const dates = [];
      const firstDay = new Date(this.selectedYear, this.selectedMonth, 1);
      const lastDay = new Date(this.selectedYear, this.selectedMonth + 1, 0);
      const startDate = new Date(firstDay);
      startDate.setDate(startDate.getDate() - firstDay.getDay());
      
      for (let i = 0; i < 42; i++) {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + i);
        dates.push({
          day: date.getDate(),
          month: date.getMonth(),
          year: date.getFullYear(),
          date: new Date(date),
          currentMonth: date.getMonth() === this.selectedMonth,
          key: `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`
        });
      }
      return dates;
    },
    years() {
      const startYear = Math.floor(this.selectedYear / 10) * 10;
      const years = [];
      for (let i = startYear; i < startYear + 10; i++) {
        years.push(i);
      }
      return years;
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        // Always update displayValue when value prop changes, even if they're currently equal
        this.displayValue = newVal || '';
        if (newVal && this.isValidDate(newVal)) {
          // Update the selected date for the picker
          const [month, day, year] = newVal.split('/').map(Number);
          this.selectedDate = new Date(year, month - 1, day);
          this.selectedMonth = month - 1;
          this.selectedYear = year;
        }
      }
    },
    podDateMissing: {
      immediate: true,
      handler(newVal) {
        if(newVal)
          this.validateDate();
        }
      }
  },
  mounted() {
    // Ensure displayValue is set from the value prop on mount
    if (this.value) {
      this.displayValue = this.value;
      // Also update picker state if it's a valid date
      if (this.isValidDate(this.value)) {
        const [month, day, year] = this.value.split('/').map(Number);
        this.selectedDate = new Date(year, month - 1, day);
        this.selectedMonth = month - 1;
        this.selectedYear = year;
      }
    }
    
    // Close date picker when clicking outside
    document.addEventListener('click', this.handleOutsideClick);
    // Reposition on window resize
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleOutsideClick);
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    handleOutsideClick(event) {
      if (!this.$el.contains(event.target)) {
        this.showDatePicker = false;
      }
    },
    
    handleResize() {
      if (this.showDatePicker) {
        this.$nextTick(() => {
          this.positionPicker();
        });
      }
    },
    onFocus() {
      this.isFocused = true;
    },
    
    onBlur() {
      this.isFocused = false;     
      this.validateDate();
      if (this.compName === 'ClientCommunication' && (!val || val.trim() === '')) {
        this.hasError = false;
        this.errorMessage = '';
      }
    },

     validateDate() {
      // Don't validate if we're currently selecting from picker
      if (this.isSelectingFromPicker) {
        return;
      }
      
      // Ensure displayValue is a string
      let value = this.displayValue;
      if (typeof value !== 'string') {
        value = value ? String(value) : '';
      }
      
      if (!value || value.trim() === '') {
        if (this.required) {
          this.hasError = true;
          this.errorMessage = 'Date is required';
        }
        return;
      }
          
      if (this.isValidDate(value)) {
        // Update picker state
        let [month, day, year] = value.split('/').map(Number);
        if(year < 100){
          year += 2000; // Auto-correct 2-digit years to 2000s
        }
        this.selectedDate = new Date(year, month - 1, day);
        this.selectedMonth = month - 1;
        this.selectedYear = year;
        
        const formattedMonth = String(this.selectedMonth + 1).padStart(2, '0');
        const formattedDay = String(this.selectedDate.getDate()).padStart(2, '0');
        this.displayValue = formattedMonth + '/' + formattedDay + '/' + this.selectedYear;
        
        // Emit events only on blur, not during validation
        if (!this.isFocused && this.displayValue.length===10) {
          this.$emit('input', this.displayValue);
          this.$emit('change', this.displayValue);
        }
        this.hasError = false;
        this.errorMessage = '';
        
      } else {
        this.hasError = true;
        this.errorMessage = 'Please enter a valid date (MM/DD/YYYY)';
      }
    },
    isValidDate(dateString) {
      if (!dateString) return false;
      
      // Ensure it's a string
      if (typeof dateString !== 'string') {
        dateString = String(dateString);
      }
      
      // Check MM/DD/YYYY or MM/DD/YY format
      const regex = /^(0?[1-9]|1[0-2])\/(0?[1-9]|[12]\d|3[01])\/\d{2}(\d{2})?$/;
      if (!regex.test(dateString)) return false;
      // Check if it's a real date
      let [month, day, year] = dateString.split('/').map(Number);
      if (year < 100) {
        year += 2000;
      }
      const date = new Date(year, month - 1, day);

      
      return date.getFullYear() === year &&
             date.getMonth() === month - 1 &&
             date.getDate() === day;
    },
    
    toggleDatePicker(event) {
      event.preventDefault();
      this.showDatePicker = !this.showDatePicker;
      this.currentView = 'date';
      
      if (this.showDatePicker) {
        if (this.displayValue && this.isValidDate(this.displayValue)) {
          const [month, day, year] = this.displayValue.split('/').map(Number);
          this.selectedDate = new Date(year, month - 1, day);
          this.selectedMonth = month - 1;
          this.selectedYear = year;
        }
        
        // Position the picker
        this.$nextTick(() => {
          this.positionPicker();
        });
      }
    },
    
    positionPicker() {
      const pickerEl = this.$el.querySelector('.custom-date-picker');
      const inputContainer = this.$el.querySelector('.floating-input-container');
      
      if (pickerEl && inputContainer) {
        const containerRect = inputContainer.getBoundingClientRect();
        const pickerRect = pickerEl.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;
        
        // Check if we're inside a dialog
        const dialogEl = this.$el.closest('.v-dialog');
        const isInDialog = !!dialogEl;
        
        // Position right below the input field
        let top = containerRect.bottom + 8;
        let left = containerRect.left;
        
        // For small screens or dialogs, center the picker
        if (viewportWidth <= 480 || isInDialog) {
          const availableWidth = isInDialog ? 
            dialogEl.querySelector('.dialog-body').clientWidth : 
            viewportWidth;
          
          if (pickerRect.width > availableWidth - 40) {
            left = Math.max(20, (viewportWidth - pickerRect.width) / 2);
          }
        }
        
        // Check if picker would go below viewport
        if (top + pickerRect.height > viewportHeight - 20) {
          // Position above the input field instead
          top = containerRect.top - pickerRect.height - 8;
        }
        
        // Check if picker would go outside right edge
        if (left + pickerRect.width > viewportWidth - 20) {
          left = containerRect.right - pickerRect.width;
        }
        
        // Check if picker would go outside left edge
        if (left < 20) {
          left = 20;
        }
        
        // Ensure top doesn't go negative
        if (top < 20) {
          top = 20;
        }
        
        pickerEl.style.top = `${Math.max(top, 20)}px`;
        pickerEl.style.left = `${Math.max(left, 20)}px`;
        pickerEl.style.right = 'auto';
        pickerEl.style.bottom = 'auto';
      }
    },
    
    toggleView() {
      if (this.currentView === 'date') {
        this.currentView = 'month';
      } else if (this.currentView === 'month') {
        this.currentView = 'year';
      } else {
        this.currentView = 'date';
      }
    },
    
    previousPeriod() {
      if (this.currentView === 'date') {
        this.selectedMonth--;
        if (this.selectedMonth < 0) {
          this.selectedMonth = 11;
          this.selectedYear--;
        }
      } else if (this.currentView === 'month') {
        this.selectedYear--;
      } else {
        this.selectedYear -= 10;
      }
    },
    
    nextPeriod() {
      if (this.currentView === 'date') {
        this.selectedMonth++;
        if (this.selectedMonth > 11) {
          this.selectedMonth = 0;
          this.selectedYear++;
        }
      } else if (this.currentView === 'month') {
        this.selectedYear++;
      } else {
        this.selectedYear += 10;
      }
    },
    
    selectDate(dateObj) {
      // Check if date is disabled
      if (this.isDateDisabled(dateObj)) {
        return;
      }
      
      if (!dateObj.currentMonth) {
        // If clicking on a date from another month, navigate to that month
        this.selectedMonth = dateObj.month;
        this.selectedYear = dateObj.year;
        return;
      }
      
      this.selectedDate = dateObj.date;
      
      // Format date consistently using UTC to avoid timezone issues
      const year = dateObj.date.getFullYear();
      const month = String(dateObj.date.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.date.getDate()).padStart(2, '0');
      const formatted = `${month}/${day}/${year}`;
      
      this.displayValue = formatted;
      this.$emit('input', formatted);
      this.$emit('change', formatted);
      this.hasError = false;
      this.showDatePicker = false;
    },
    
    selectMonth(month) {
      this.selectedMonth = month.value;
      this.currentView = 'date';
    },
    
    selectYear(year) {
      this.selectedYear = year;
      this.currentView = 'month';
    },
    
    isSelectedDate(dateObj) {
      if (!this.selectedDate || !dateObj.currentMonth) return false;
      return dateObj.date.getTime() === this.selectedDate.getTime();
    },
    
    isToday(dateObj) {
      const today = new Date();
      const todayUTC = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));
      const dateUTC = new Date(Date.UTC(dateObj.date.getFullYear(), dateObj.date.getMonth(), dateObj.date.getDate()));
      return dateUTC.getTime() === todayUTC.getTime();
    },
    
    isDateDisabled(dateObj) {
      const dateToCheck = new Date(dateObj.date.getFullYear(), dateObj.date.getMonth(), dateObj.date.getDate());
      dateToCheck.setHours(0, 0, 0, 0);
      
      // Check minDate
      if (this.minDate) {
        const minDateObj = this.parseDate(this.minDate);
        if (minDateObj) {
          minDateObj.setHours(0, 0, 0, 0);
          if (dateToCheck < minDateObj) {
            return true;
          }
        }
      }
      
      // Check maxDate
      if (this.maxDate) {
        const maxDateObj = this.parseDate(this.maxDate);
        if (maxDateObj) {
          maxDateObj.setHours(0, 0, 0, 0);
          if (dateToCheck > maxDateObj) {
            return true;
          }
        }
      }
      
      return false;
    },
    
    parseDate(dateString) {
      if (!dateString) return null;
      
      // Handle MM/DD/YYYY and MM/DD/YY formats
      const match = dateString.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/);
      if (match) {
        const [, month, day, yearPart] = match;
        let year = parseInt(yearPart);
        if (year < 100) {
          year += 2000;
        }
        return new Date(year, parseInt(month) - 1, parseInt(day));
      }
      
      return null;
    },
    
    onInputChange(event) {
      const val = event.target.value || '';
      this.displayValue = val;
      this.validateDate();
      if (this.compName === 'ClientCommunication' && (!val || val.trim() === '')) {
        this.hasError = false;
        this.errorMessage = '';
      }
      this.$emit('input', val);
      this.$emit('change', val);
    },
  }
}
</script>

<style scoped>
.date-component {
  position: relative;
}

.floating-input-container {
  position: relative;
  margin-bottom: 16px;
}

.date-input {
  width: 100%;
  padding: 16px 40px 8px 16px;
  border: 1px solid #5A5A5A;
  border-radius: 4px;
  font-size: 16px;
  background: transparent;
  transition: border-color 0.3s ease;
  box-sizing: border-box;
}

.date-input:focus {
  outline: none;
  border-color: #5A5A5A;
}

.date-input.error {
  border-color: #f44336;
}

.floating-label {
  position: absolute;
  left: 16px;
  top: 12px;
  font-size: 14px;
  color: #757575;
  transition: all 0.3s ease;
  pointer-events: none;
  background: white;
  padding: 0 4px;
}

.floating-label.float {
  top: -8px;
  font-size: 12px;
  color: #003F2D;
}

.date-input.error + .floating-label.float {
  color: #f44336;
}

.calendar-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  color: #5A5A5A;
  width: 20px;
  height: 20px;
}

.calendar-icon:hover {
  color: #003F2D;
}

.error-message {
  color: #f44336;
  font-size: 12px;
  margin-top: 4px;
  display: block;
}

/* Custom Date Picker Styles */
.custom-date-picker {
  position: fixed;
  top: 0;
  left: 0;
  background: white;
  border: none;
  border-radius: 12px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05);
  z-index: 99999;
  min-width: 320px;
  max-width: 90vw;
  width: auto;
  overflow: hidden;
}

.picker-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: white;
  color: #003F2D;
}

.nav-button {
  background: #f5f5f5;
  border: none;
  color: #003F2D;
  font-size: 18px;
  cursor: pointer;
  padding: 8px 10px;
  border-radius: 8px;
  min-width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.nav-button:hover {
  background: #e8f5e8;
  transform: scale(1.05);
}

.nav-button:active {
  transform: scale(0.95);
}

.period-display {
  font-weight: 600;
  cursor: pointer;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 16px;
  text-align: center;
  flex: 1;
  margin: 0 12px;
  background: #f8f9fa;
  color: #003F2D;
  transition: all 0.2s ease;
}

.period-display:hover {
  background: #e8f5e8;
  transform: translateY(-1px);
}

.date-grid {
  padding: 20px;
  background: white;
}

.weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
  margin-bottom: 12px;
}

.weekday {
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  color: #003F2D;
  padding: 8px 2px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.dates {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
}

.date-cell {
  text-align: center;
  padding: 10px 4px;
  cursor: pointer;
  border-radius: 8px;
  font-size: 14px;
  min-height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  transition: all 0.2s ease;
  position: relative;
}

.date-cell:hover {
  background: rgba(0, 63, 45, 0.1);
  transform: scale(1.05);
}

.date-cell.other-month {
  color: #bbb;
  opacity: 0.5;
}

.date-cell.selected {
  background: linear-gradient(135deg, #003F2D 0%, #002620 100%);
  color: white;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(0, 63, 45, 0.3);
}

.date-cell.today {
  font-weight: 700;
  color: #003F2D;
  background: rgba(0, 63, 45, 0.1);
  border: 2px solid #003F2D;
}

.date-cell.today.selected {
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.3);
}

.date-cell.disabled {
  color: #ccc;
  cursor: not-allowed;
  background: #f5f5f5;
  opacity: 0.5;
  pointer-events: none;
}

.date-cell.disabled:hover {
  background: #f5f5f5;
  transform: none;
}

.month-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  padding: 20px;
  background: white;
}

.month-cell {
  text-align: center;
  padding: 12px 8px;
  cursor: pointer;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
}

.month-cell:hover {
  background: rgba(25, 118, 210, 0.1);
  transform: translateY(-2px);
}

.month-cell.selected {
  background: linear-gradient(135deg, #003F2D 0%, #002620 100%);
  color: white;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(0, 63, 45, 0.3);
}

.year-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  padding: 20px;
  background: white;
}

.year-cell {
  text-align: center;
  padding: 12px 8px;
  cursor: pointer;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
}

.year-cell:hover {
  background: rgba(0, 63, 45, 0.1);
  transform: translateY(-2px);
}

@media (max-width: 480px) {
  .custom-date-picker {
    max-width: calc(100vw - 20px);
    min-width: calc(100vw - 40px);
    width: calc(100vw - 40px);
  }
  
  .date-cell {
    font-size: 12px;
    padding: 8px 2px;
    min-height: 32px;
  }
  
  .weekday {
    font-size: 10px;
    padding: 6px 2px;
  }
  
  .picker-header {
    padding: 12px 16px;
  }
  
  .period-display {
    font-size: 14px;
    margin: 0 8px;
    padding: 6px 12px;
  }
  
  .nav-button {
    font-size: 16px;
    padding: 6px 8px;
    min-width: 32px;
    height: 32px;
  }
  
  .date-grid {
    padding: 16px;
  }
  
  .month-grid, .year-grid {
    padding: 16px;
  }
  
  .month-cell, .year-cell {
    padding: 10px 6px;
    font-size: 12px;
  }
}

@media (max-width: 350px) {
  .floating-input-container{
    margin-bottom: 8px !important;
  }
}

@media (max-width: 320px) {
  .floating-input-container{
    margin-bottom: 8px !important;
  }
}
</style>
